import { useCallback, useEffect, useRef, useState } from 'react';
import {
  VARIABLE_COL_MIN,
  VARIABLE_FLEX_COL_COUNT,
  adjustAdjacentColumnWidths,
  normalizeVariableColumnWidths,
  variableColumnsTemplate,
} from '../../../../utils/variableColumnWidths';

const contentHeaderCells = (tableEl) => {
  const header = tableEl?.querySelector('.variables-panel__table-header');
  if (!header) return [];
  return [...header.querySelectorAll(':scope > .variables-panel__col-shell')];
};

const measureContentCols = (tableEl) => {
  const cells = contentHeaderCells(tableEl);
  if (cells.length < VARIABLE_FLEX_COL_COUNT) return null;
  return cells.slice(0, VARIABLE_FLEX_COL_COUNT).map((el) =>
    Math.max(1, Math.round(el.getBoundingClientRect().width))
  );
};

const paintContentCols = (tableEl, widths) => {
  if (!tableEl || !widths?.length) return;
  const includeDrag = !tableEl.classList.contains('variables-panel__table--single');
  tableEl.style.setProperty(
    '--vp-grid-cols',
    variableColumnsTemplate(widths, { includeDrag })
  );
};

/**
 * Same divider logic as doc tables: measure live px, trade width between
 * the two neighbors, keep the grid at 100% of the table.
 */
export default function useColumnResize(columnWidths, onColumnWidthsChange) {
  const [localWidths, setLocalWidths] = useState(() =>
    normalizeVariableColumnWidths(columnWidths)
  );
  const [isResizing, setIsResizing] = useState(false);
  const widthsRef = useRef(localWidths);
  const tableRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    if (dragRef.current) return;
    const next = normalizeVariableColumnWidths(columnWidths);
    widthsRef.current = next;
    setLocalWidths(next);
  }, [columnWidths]);

  const startColumnResize = useCallback(
    (leftIndex, e) => {
      if (!onColumnWidthsChange) return;
      if (e.button != null && e.button !== 0) return;
      if (leftIndex < 0 || leftIndex >= VARIABLE_FLEX_COL_COUNT - 1) return;
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget?.setPointerCapture?.(e.pointerId);

      const startWidths = measureContentCols(tableRef.current);
      if (!startWidths) return;

      dragRef.current = { leftIndex, startX: e.clientX, startWidths, current: startWidths };
      const prevCursor = document.body.style.cursor;
      const prevSelect = document.body.style.userSelect;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      setIsResizing(true);
      paintContentCols(tableRef.current, startWidths);
      widthsRef.current = startWidths;
      setLocalWidths(startWidths);

      const floor = Math.max(32, Math.min(VARIABLE_COL_MIN, ...startWidths));

      const onMove = (ev) => {
        const drag = dragRef.current;
        if (!drag) return;
        const next = adjustAdjacentColumnWidths(
          drag.startWidths,
          drag.leftIndex,
          ev.clientX - drag.startX,
          floor
        );
        drag.current = next;
        widthsRef.current = next;
        paintContentCols(tableRef.current, next);
        setLocalWidths(next);
      };

      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        document.body.style.cursor = prevCursor;
        document.body.style.userSelect = prevSelect;
        const latest = dragRef.current?.current;
        dragRef.current = null;
        setIsResizing(false);
        if (!latest) return;
        const normalized = normalizeVariableColumnWidths(latest);
        widthsRef.current = normalized;
        setLocalWidths(normalized);
        onColumnWidthsChange(normalized);
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [onColumnWidthsChange]
  );

  return {
    localWidths,
    tableRef,
    canResize: !!onColumnWidthsChange,
    isResizing,
    startColumnResize,
  };
}
